/**
 * Calendar days for the home timeline. A "day" is `YYYY-MM-DD` in the GAMES'
 * time zone — the API counts `from` / `to` there (never the server's, never
 * the viewer's). Times on screen are still shown in the viewer's own zone.
 *
 * Pure functions, no React: unit-tested in `day.test.ts`.
 */

const DAY = /^\d{4}-\d{2}-\d{2}$/;

/** A real calendar day as `YYYY-MM-DD` — not 30 February (which `Date.parse` quietly rolls into March). */
export const isDay = (v: unknown): v is string => {
	if (typeof v !== "string" || !DAY.test(v)) return false;
	const t = Date.parse(`${v}T00:00:00Z`);
	return !Number.isNaN(t) && new Date(t).toISOString().slice(0, 10) === v;
};

const zoneFormatters = new Map<string, Intl.DateTimeFormat>();

/** The calendar day `instant` falls on in `timeZone`. Machine format, so a fixed locale (not the viewer's). */
export function dayInZone(instant: Date | string, timeZone: string): string {
	let f = zoneFormatters.get(timeZone);
	if (!f) {
		f = new Intl.DateTimeFormat("en-CA", { timeZone, year: "numeric", month: "2-digit", day: "2-digit" });
		zoneFormatters.set(timeZone, f);
	}
	const parts = f.formatToParts(typeof instant === "string" ? new Date(instant) : instant);
	const get = (type: string) => parts.find((p) => p.type === type)?.value ?? "";
	return `${get("year")}-${get("month")}-${get("day")}`;
}

/** `day` plus `n` days (n may be negative). */
export function addDays(day: string, n: number): string {
	const d = new Date(`${day}T00:00:00Z`);
	d.setUTCDate(d.getUTCDate() + n);
	return d.toISOString().slice(0, 10);
}

/** A games runs for days, not months: the picker never lists more than this. */
export const MAX_GAMES_DAYS = 45;

/** Every day from `first` to `last`, inclusive, at most `MAX_GAMES_DAYS`. Empty when `last` is before `first`. */
export function daysBetween(first: string, last: string): string[] {
	const out: string[] = [];
	for (let d = first; d <= last && out.length < MAX_GAMES_DAYS; d = addDays(d, 1)) out.push(d);
	return out;
}

/** Today when the games run today, else the first competition day (else today, when nothing is known). */
export function defaultDay(today: string, days: readonly string[]): string {
	if (days.includes(today)) return today;
	return days[0] ?? today;
}

/** The API's one-day range: `from` inclusive, `to` exclusive. */
export const dayRange = (day: string): { from: string; to: string } => ({ from: day, to: addDays(day, 1) });
